import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTRACT_ACCEPT = ".pdf,.docx,.doc,.png,.jpg,.jpeg";

async function uploadContract(baaId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API}/baas/${baaId}/contract`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return res.data;
}

function ContractCell({ baa, onSaved }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    try {
      const res = await axios.get(`${API}/baas/${baa.id}/contract`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = baa.contract_filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };
  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    uploadContract(baa.id, file)
      .then((d) => { onSaved(d); toast.success(`BAA contract for ${baa.vendor} on file`); })
      .catch((err) => toast.error(err.response?.data?.detail || "Upload failed"))
      .finally(() => { setBusy(false); e.target.value = ""; });
  };
  return (
    <>
      {baa.contract_filename ? (
        <>
          <button className="vr-link" onClick={download} data-testid={`baa-contract-download-${baa.vendor}`}>{baa.contract_filename} ⬇</button>
          <div className="cap-sub">on file {baa.contract_uploaded_at ? baa.contract_uploaded_at.slice(0, 10) : ""}</div>
        </>
      ) : (
        <span className="cap-sub" data-testid={`baa-contract-missing-${baa.vendor}`}>not uploaded</span>
      )}
      <label className="vr-link" style={{ cursor: "pointer", display: "block" }}>
        {busy ? "Uploading…" : baa.contract_filename ? "Replace…" : "Upload contract…"}
        <input type="file" accept={CONTRACT_ACCEPT} style={{ display: "none" }} onChange={pick} disabled={busy} data-testid={`baa-contract-upload-${baa.vendor}`} />
      </label>
    </>
  );
}

function RenewControls({ baa, onSaved }) {
  const [open, setOpen] = useState(false);
  const [renewal, setRenewal] = useState("");
  const save = async () => {
    try {
      const res = await axios.put(`${API}/baas/${baa.id}`, { renewal_date: renewal });
      onSaved(res.data);
      toast.success(`BAA with ${baa.vendor} renewal set to ${renewal}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  if (!open) return <button className="vr-link" onClick={() => setOpen(true)} data-testid={`baa-renew-${baa.vendor}`}>Update renewal…</button>;
  return (
    <div className="vr-form">
      <input className="inp" type="date" value={renewal} onChange={(e) => setRenewal(e.target.value)} data-testid={`baa-renew-date-${baa.vendor}`} />
      <div className="vr-btnrow">
        <button className="btn" onClick={save} disabled={!renewal} data-testid={`baa-renew-save-${baa.vendor}`}>Save</button>
        <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default function BaaInventory({ active }) {
  const [rows, setRows] = useState(null);
  const [vendor, setVendor] = useState("");
  const [service, setService] = useState("");
  const [signed, setSigned] = useState("");
  const [renewal, setRenewal] = useState("");
  const [contact, setContact] = useState("");
  const [scope, setScope] = useState("");
  const [contractFile, setContractFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchRows = useCallback(() => {
    axios.get(`${API}/baas`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { if (active === "baa") fetchRows(); }, [active, fetchRows]);

  const canSubmit = vendor.trim() && signed && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/baas`, { vendor, service, baa_signed_date: signed, renewal_date: renewal, contact, phi_scope: scope });
      if (contractFile) {
        try {
          await uploadContract(res.data.id, contractFile);
          toast.success(`BAA with ${vendor} added — signed contract on file`);
        } catch (upErr) {
          toast.error(`${vendor} added, but the contract upload failed: ${upErr.response?.data?.detail || "upload error"}. Use "Upload contract…" on the row.`);
        }
      } else {
        toast.success(`BAA with ${vendor} added to the inventory`);
      }
      setVendor(""); setService(""); setSigned(""); setRenewal(""); setContact(""); setScope(""); setContractFile(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add BAA");
    }
    setSubmitting(false);
  };

  const onSaved = (d) => setRows((p) => p.map((x) => (x.id === d.id ? d : x)));
  const pill = (s) => (s === "LAPSED" ? "declined" : s === "RENEW SOON" ? "open" : "cleared");

  return (
    <section className={`chapter utility${active === "baa" ? " active" : ""}`} id="baa">
      <div className="eyebrow">Working record · executes ECH-SOP-6.3 — no PHI flows to a vendor without a current BAA</div>
      <h1>BAA Inventory</h1>
      <p className="lede">Every business associate that touches PHI — billing/RCM vendor, EHR, cloud storage, shredding, counsel where applicable — with the signed BAA date and its renewal. A lapsed BAA stops the PHI flow the same day, and the reminders bar warns 30 days out.</p>

      <div className="formcard" data-testid="baa-form">
        <div className="formcard-title">Add a business associate</div>
        <div className="fld-grid three">
          <div className="fld"><label>Vendor</label><input className="inp" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Company name" data-testid="baa-vendor" /></div>
          <div className="fld"><label>Service</label><input className="inp" value={service} onChange={(e) => setService(e.target.value)} placeholder="e.g. Billing / RCM" data-testid="baa-service" /></div>
          <div className="fld"><label>BAA signed</label><input className="inp" type="date" value={signed} onChange={(e) => setSigned(e.target.value)} data-testid="baa-signed" /></div>
        </div>
        <div className="fld-grid three">
          <div className="fld"><label>Renewal / expiry (optional)</label><input className="inp" type="date" value={renewal} onChange={(e) => setRenewal(e.target.value)} data-testid="baa-renewal" /></div>
          <div className="fld"><label>Vendor contact</label><input className="inp" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Name / email" data-testid="baa-contact" /></div>
          <div className="fld"><label>PHI scope</label><input className="inp" value={scope} onChange={(e) => setScope(e.target.value)} placeholder="What PHI they receive" data-testid="baa-scope" /></div>
        </div>
        <div className="fld" style={{ marginBottom: 12 }}>
          <label>Upload BAA contract (the signed agreement — PDF, DOCX, DOC, PNG, JPG · max 20 MB)</label>
          <input className="inp" type="file" accept={CONTRACT_ACCEPT} onChange={(e) => setContractFile(e.target.files?.[0] || null)} data-testid="baa-contract-file" />
          {contractFile ? <div className="cap-sub">Attaching: {contractFile.name} ({(contractFile.size / 1024).toFixed(0)} KB)</div> : <div className="cap-sub">Optional but expected — an inventory row without the signed agreement behind it is a claim, not evidence.</div>}
        </div>
        <button className="btn" onClick={submit} disabled={!canSubmit} data-testid="baa-submit">Add to inventory</button>
      </div>

      <h2>Inventory {rows ? `— ${rows.length} vendor${rows.length === 1 ? "" : "s"}` : ""}</h2>
      <table data-testid="baa-table">
        <tbody>
          <tr><th>Vendor</th><th>Service</th><th>PHI scope</th><th>Signed</th><th>Renewal</th><th>Contract</th><th style={{ width: 210 }}>Status</th></tr>
          {rows === null ? (
            <tr><td colSpan={7}>Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={7}>No business associates on the inventory.</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                <td>{r.vendor}{r.contact ? <div className="cap-sub">{r.contact}</div> : null}</td>
                <td>{r.service || "—"}</td>
                <td>{r.phi_scope || "—"}</td>
                <td>{r.baa_signed_date}</td>
                <td>{r.renewal_date || "—"}</td>
                <td><ContractCell baa={r} onSaved={onSaved} /></td>
                <td>
                  <span className={`vr-pill ${pill(r.status)}`} data-testid={`baa-status-${r.vendor}`}>{r.status}{r.days_left != null ? ` (${r.days_left}d)` : ""}</span>
                  <RenewControls baa={r} onSaved={onSaved} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Retention: HIPAA documentation 6 years from last effective date (SOP 6.6). Inventory reconciles against the vendor list at MOCR question B-5.</p>
    </section>
  );
}
